"use client";

import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from "react";
import {
  SymAufgabenliste,
  SymListe,
  SymNummerierung,
  SymTrennlinie,
  SymUeberschrift1,
  SymUeberschrift2,
  SymUeberschrift3,
  SymZitat,
} from "./symbole";

/** Eintrag des Slash-Menüs (so viel kennt die Darstellung; `@horn/editor` reicht mehr durch, das spielt hier keine Rolle). */
export interface SlashPunkt {
  key: string;
  label: string;
}

export interface SlashMenueEigenschaften {
  items: SlashPunkt[];
  /** Methodensyntax mit Absicht: der Editor reicht einen Befehl für sein volleres Eintragsobjekt durch (Parameter-Bivarianz). */
  command(item: SlashPunkt): void;
}

export interface SlashMenueRef {
  onKeyDown: (opts: { event: KeyboardEvent }) => boolean;
}

// Fläche und Einträge wie der Auswahl-Block der Preline-Auswahlmenüs (`AnsichtWahl`: hs-select dropdownClasses/optionClasses).
const FLAECHE_KLASSE = "w-60 max-h-72 overflow-y-auto p-1 space-y-0.5 bg-select border border-select-line rounded-xl shadow-xl";
const EINTRAG_KLASSE =
  "py-2 px-3 w-full flex items-center gap-x-2 text-sm text-start text-select-item-foreground cursor-pointer hover:bg-select-item-hover rounded-lg";
const EINTRAG_AKTIV_KLASSE =
  "py-2 px-3 w-full flex items-center gap-x-2 text-sm text-start text-select-item-foreground cursor-pointer bg-select-item-active hover:bg-select-item-hover rounded-lg";
const LEER_KLASSE = "py-2 px-3 text-sm text-muted-foreground-1";

function symbolFuer(key: string): ReactNode {
  switch (key) {
    case "h1":
      return <SymUeberschrift1 />;
    case "h2":
      return <SymUeberschrift2 />;
    case "h3":
      return <SymUeberschrift3 />;
    case "list":
      return <SymListe />;
    case "ordered":
      return <SymNummerierung />;
    case "checklist":
      return <SymAufgabenliste />;
    case "quote":
      return <SymZitat />;
    case "hr":
      return <SymTrennlinie />;
    default:
      return null;
  }
}

/**
 * Darstellung des Slash-Menüs in der Preline-Hülle (E3). Die Liste schwebt am Cursor (Portal und Position setzt
 * `@horn/editor`); der Fokus bleibt im Editor, deshalb bedient der Editor die Tastatur über `ref.onKeyDown`
 * (Pfeil hoch/runter, Enter; Esc schließt das Menü im Editor). Wahl per Maus über `onMouseDown` (der Editor behält den
 * Fokus). Die Hervorhebung folgt der Tastatur und scrollt mit.
 */
export const SlashMenue = forwardRef<SlashMenueRef, SlashMenueEigenschaften>(function SlashMenue(
  { items, command },
  ref,
) {
  const [index, setIndex] = useState(0);
  const aktiv = useRef<HTMLDivElement | null>(null);

  useEffect(() => setIndex(0), [items]);
  useEffect(() => {
    aktiv.current?.scrollIntoView?.({ block: "nearest" });
  }, [index]);

  useImperativeHandle(ref, () => ({
    onKeyDown({ event }) {
      if (event.key === "ArrowDown") {
        setIndex((i) => (i + 1) % Math.max(items.length, 1));
        return true;
      }
      if (event.key === "ArrowUp") {
        setIndex((i) => (i - 1 + items.length) % Math.max(items.length, 1));
        return true;
      }
      if (event.key === "Enter") {
        const punkt = items[index];
        if (punkt) command(punkt);
        return true;
      }
      return false;
    },
  }));

  return (
    <>
      {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-select-menus/view-mode-select */}
      <div className={FLAECHE_KLASSE} role="listbox" aria-label="Textbausteine">
        {items.length === 0 && <div className={LEER_KLASSE}>Keine Treffer</div>}
        {items.map((punkt, i) => (
          <div
            key={punkt.key}
            ref={i === index ? aktiv : null}
            role="option"
            aria-selected={i === index}
            className={i === index ? EINTRAG_AKTIV_KLASSE : EINTRAG_KLASSE}
            onMouseDown={(e) => {
              e.preventDefault();
              command(punkt);
            }}
          >
            {symbolFuer(punkt.key)}
            <span className="grow truncate">{punkt.label}</span>
          </div>
        ))}
      </div>
    </>
  );
});
